/**
 * Static nav configuration — single source of truth cho PrimarySidebar +
 * MobileNav + Breadcrumbs.
 *
 * Doc 28 §4 + Doc 29 §6.
 */
import {
  Activity,
  Archive,
  Bell,
  Boxes,
  Coins,
  Cpu,
  FileSearch,
  FileText,
  Globe,
  type LucideIcon,
  Languages,
  LayoutDashboard,
  MessageSquare,
  Settings,
  Sparkles,
  Users,
  Workflow,
} from "lucide-react";

import type { RouteName } from "@/stores/route";

export interface NavItem {
  route: RouteName;
  label: string;
  icon: LucideIcon;
  /** Required role; nếu thiếu role này → ẩn item. */
  requiredRole?: "admin" | "auditor";
  /** Match cho active state — supports parent route highlight (vd
   * `admin.forms` active khi route = `admin.forms.edit`). */
  matchPrefixes?: string[];
  /** Param truyền vào navigate (vd code cho admin.forms.edit). */
  param?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Workspace",
    items: [
      {
        route: "chat",
        label: "Chat",
        icon: MessageSquare,
        matchPrefixes: ["chat"],
      },
    ],
  },
  {
    label: "Build",
    items: [
      {
        route: "admin.forms",
        label: "Forms",
        icon: FileText,
        requiredRole: "admin",
        matchPrefixes: ["admin.forms"],
      },
      {
        route: "admin.workflows",
        label: "Workflows",
        icon: Workflow,
        requiredRole: "admin",
        matchPrefixes: ["admin.workflows"],
      },
      {
        route: "admin.notifications",
        label: "Notifications",
        icon: Bell,
        requiredRole: "admin",
        matchPrefixes: ["admin.notifications"],
      },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        route: "admin",
        label: "Dashboard",
        icon: LayoutDashboard,
        requiredRole: "admin",
      },
      {
        route: "admin.odooModules",
        label: "Odoo Modules",
        icon: Boxes,
        requiredRole: "admin",
        matchPrefixes: ["admin.odooModules"],
      },
      {
        route: "admin.externalSystems",
        label: "External Systems",
        icon: Globe,
        requiredRole: "admin",
        matchPrefixes: ["admin.externalSystems"],
      },
      {
        route: "admin.translations",
        label: "Translations",
        icon: Languages,
        requiredRole: "admin",
        matchPrefixes: ["admin.translations"],
      },
      {
        route: "admin.audit",
        label: "Audit Logs",
        icon: FileSearch,
        requiredRole: "admin",
        matchPrefixes: ["admin.audit"],
      },
      {
        route: "admin.dashboards",
        label: "Dashboards",
        icon: LayoutDashboard,
        requiredRole: "admin",
        matchPrefixes: ["admin.dashboards"],
      },
      {
        route: "admin.cost",
        label: "Cost Analytics",
        icon: Coins,
        requiredRole: "admin",
        matchPrefixes: ["admin.cost"],
      },
      {
        route: "admin.health",
        label: "System Health",
        icon: Activity,
        requiredRole: "admin",
        matchPrefixes: ["admin.health"],
      },
      {
        route: "admin.localLlm",
        label: "Local LLM (Ollama)",
        icon: Cpu,
        requiredRole: "admin",
        matchPrefixes: ["admin.localLlm"],
      },
      {
        route: "admin.insights",
        label: "AI Insights",
        icon: Sparkles,
        requiredRole: "admin",
        matchPrefixes: ["admin.insights"],
      },
      {
        route: "admin.users",
        label: "Users",
        icon: Users,
        requiredRole: "admin",
        matchPrefixes: ["admin.users"],
      },
      {
        route: "admin.backup",
        label: "Backup & Restore",
        icon: Archive,
        requiredRole: "admin",
        matchPrefixes: ["admin.backup"],
      },
    ],
  },
];

/** Settings pinned ở footer của PrimarySidebar. */
export const SETTINGS_ITEM: NavItem = {
  route: "settings.personal",
  label: "Cài đặt",
  icon: Settings,
  matchPrefixes: ["settings"],
};

/** Mobile bottom tabs — 5 items quan trọng nhất. */
export const MOBILE_TABS: NavItem[] = [
  {
    route: "chat",
    label: "Chat",
    icon: MessageSquare,
    matchPrefixes: ["chat"],
  },
  {
    route: "admin.forms",
    label: "Forms",
    icon: FileText,
    requiredRole: "admin",
    matchPrefixes: ["admin.forms"],
  },
  {
    route: "admin.workflows",
    label: "Flows",
    icon: Workflow,
    requiredRole: "admin",
    matchPrefixes: ["admin.workflows"],
  },
  {
    route: "admin.notifications",
    label: "Notify",
    icon: Bell,
    requiredRole: "admin",
    matchPrefixes: ["admin.notifications"],
  },
  SETTINGS_ITEM,
];

/**
 * Filter items theo role user.
 *
 * "admin" satisfy cả "admin" và "auditor" requirement (admin > auditor).
 */
export function filterByRoles(
  items: NavItem[],
  roles: string[],
): NavItem[] {
  return items.filter((item) => {
    if (!item.requiredRole) return true;
    if (item.requiredRole === "admin") return roles.includes("admin");
    if (item.requiredRole === "auditor") {
      return roles.includes("auditor") || roles.includes("admin");
    }
    return false;
  });
}

/**
 * Active state matcher — handle parent route highlight.
 *
 * VD: `admin.forms.edit` should highlight `admin.forms` item.
 */
export function isItemActive(item: NavItem, currentRoute: string): boolean {
  if (item.matchPrefixes) {
    return item.matchPrefixes.some((p) => currentRoute.startsWith(p));
  }
  return currentRoute === item.route;
}
